import React, { useState, useEffect } from "react";
import { DailyEntryTable } from "../components/dailyEntries/DailyEntryTable";
import { Pagination } from "../components/common/Pagination";
import { VerifyCustomerForm } from "../components/customers/VerifyCustomerForm";
import { useDailyEntryManagement } from "../hooks/useDailyEntries";
import { DailyEntry, GetDailyEntriesParams } from "../types/dailyEntry";
import toast from "react-hot-toast";
import { Trash2 } from "lucide-react";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { DailyEntryFilters } from "../components/dailyEntries/DailyEntryFilters";
import { useExcelExport } from "../hooks/useExcelExport";
import { useCustomerCountries } from "../hooks/useCustomers";
import { useStates } from "../hooks/useState";
import { State } from "../api/services/state/type";

export const DailyEntries: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedDateFrom, setSelectedDateFrom] = useState<string>("");
  const [selectedDateTo, setSelectedDateTo] = useState<string>("");
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
  const [selectedCountryId, setSelectedCountryId] = useState<string>("");
  const [showVerifyForm, setShowVerifyForm] = useState(false);
  const { exportToExcel } = useExcelExport();
  const [, setEditingEntry] = useState<DailyEntry | undefined>(undefined);
  const itemsPerPage = 25;

  const { data: countries } = useCustomerCountries();
  const { data: statesData } = useStates({ limit: 1000 });

  const getQueryParams = (): GetDailyEntriesParams => {
    const params: GetDailyEntriesParams = {
      skip: (currentPage - 1) * itemsPerPage,
      limit: itemsPerPage,
    };

    if (selectedDateFrom && selectedDateTo) {
      params.fromDate = selectedDateFrom;
      params.toDate = selectedDateTo;
    } else if (selectedDate) {
      params.entryDate = selectedDate;
    }

    if (selectedCustomerId) {
      params.customerId = selectedCustomerId;
    }

    if (selectedCountryId) {
      params.countryId = selectedCountryId;
    }

    return params;
  };

  const {
    dailyEntries,
    total,
    isLoading,
    isError,
    error,
    deleteDailyEntry,
    isUpdating,
    isDeleting,
    isCreatingCustomerEntry,
    updateError,
    deleteError,
    createCustomerEntryError,
    refreshDailyEntries,
    exportDailyEntries,
    isExporting,
    exportError,
  } = useDailyEntryManagement(getQueryParams());

  useEffect(() => {
    setCurrentPage(1);
  }, [
    selectedDate,
    selectedDateFrom,
    selectedDateTo,
    selectedCustomerId,
    selectedCountryId,
  ]);

  const totalPages = Math.max(1, Math.ceil((total || 0) / itemsPerPage));

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    setSelectedDateFrom("");
    setSelectedDateTo("");
  };

  const handleDateRangeChange = (dateFrom: string, dateTo: string) => {
    setSelectedDateFrom(dateFrom);
    setSelectedDateTo(dateTo);
    setSelectedDate("");
  };

  const handleDateClear = () => {
    setSelectedDate("");
    setSelectedDateFrom("");
    setSelectedDateTo("");
  };

  const handleCountryChange = (countryId: string) => {
    setSelectedCountryId(countryId);
  };

  const handleClearAllFilters = () => {
    setSelectedDate("");
    setSelectedDateFrom("");
    setSelectedDateTo("");
    setSelectedCustomerId("");
    setSelectedCountryId("");
  };

  const handleCreateEntry = () => {
    setShowVerifyForm(true);
  };

  const handleEditEntry = (entry: DailyEntry) => {
    setEditingEntry(entry);
    console.log("Edit entry:", entry);
    toast.success("Edit functionality not implemented yet");
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<string | null>(null);

  const handleDeleteEntry = (entryId: string) => {
    setEntryToDelete(entryId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (entryToDelete) {
      deleteDailyEntry(entryToDelete, {
        onSuccess: () => {
          toast.success("Daily entry deleted successfully!");
          setDeleteDialogOpen(false);
          setEntryToDelete(null);
        },
        onError: (error) => {
          toast.error(`Failed to delete entry: ${error.message}`);
        },
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setEntryToDelete(null);
  };

  const handleViewEntry = (entry: DailyEntry) => {
    console.log("View entry:", entry);
    toast.success("Entry details (check console)");
  };

  const handleVerifySuccess = () => {
    setShowVerifyForm(false);
    refreshDailyEntries();
  };

  const handleVerifyError = (error: string) => {
    toast.error(`Failed to create customer entry: ${error}`);
  };

  const handleVerifyCancel = () => {
    setShowVerifyForm(false);
    setEditingEntry(undefined);
  };

  const handleExportExcel = async () => {
    try {
      toast.loading("Preparing export...", { id: "export-loading" });

      const exportParams = getQueryParams();
      delete exportParams.skip;
      delete exportParams.limit;

      const exportData = await exportDailyEntries();

      if (exportData?.dailyEntries) {
        const countriesMap = new Map();
        const statesMap = new Map();

        if (countries) {
          countries.forEach((country) => {
            countriesMap.set(country.id, country.name);
          });
        }

        if (statesData?.states) {
          statesData.states.forEach((state: State) => {
            statesMap.set(state.id, state.name);
          });
        }

        const data = exportData.dailyEntries.map((entry, index) => {
          const countryName =
            countriesMap.get(entry.customer.countryId) || "N/A";
          const stateName = entry.customer.stateId
            ? statesMap.get(entry.customer.stateId) || "N/A"
            : "N/A";

          const entryDate = new Date(entry.entryDate);
          const formattedDate = entryDate.toLocaleDateString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          });

          return {
            SN: index + 1,
            "Customer Name": entry.customer.name || "N/A",
            Address: stateName,
            Nationality: countryName,
            "Customer ID": entry.customer.playerId || "N/A",
            "Purpose of Entry": "",
            "Date of Entry": formattedDate,
            "Time of Exit": "",
            Remarks: "",
          };
        });

        exportToExcel({
          data,
          filename: "daily_entries",
          sheetName: "Daily Entries",
          showToast: false,
        });

        toast.success("Excel file exported successfully!", {
          id: "export-loading",
        });
      } else {
        toast.error("No data to export", { id: "export-loading" });
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data", { id: "export-loading" });
    }
  };

  if (isError) {
    return (
      <div className="min-h-screen bg-slate-900 p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p>
            Error loading daily entries: {error?.message || "Unknown error"}
          </p>
          <button
            onClick={refreshDailyEntries}
            className="mt-2 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Filters */}
        <DailyEntryFilters
          selectedDate={selectedDate}
          selectedDateFrom={selectedDateFrom}
          selectedDateTo={selectedDateTo}
          selectedCustomerId={selectedCustomerId}
          selectedCountryId={selectedCountryId}
          isCreatingCustomerEntry={isCreatingCustomerEntry}
          onDateChange={handleDateChange}
          onDateRangeChange={handleDateRangeChange}
          onDateClear={handleDateClear}
          onCountryChange={handleCountryChange}
          onClearAll={handleClearAllFilters}
          onVerifyCustomer={handleCreateEntry}
          onExportExcel={handleExportExcel}
        />

        {/* Error Messages */}
        {createCustomerEntryError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            Create Error: {createCustomerEntryError.message}
          </div>
        )}
        {updateError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            Update Error: {updateError.message}
          </div>
        )}
        {deleteError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            Delete Error: {deleteError.message}
          </div>
        )}
        {exportError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            Export Error: {exportError.message}
          </div>
        )}

        {/* Loading States */}
        {(isDeleting ||
          isUpdating ||
          isCreatingCustomerEntry ||
          isExporting) && (
          <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4">
            {isDeleting && "Deleting entry..."}
            {isUpdating && "Updating entry..."}
            {isCreatingCustomerEntry && "Creating entry..."}
            {isExporting && "Exporting data..."}
          </div>
        )}

        {/* Daily Entry Table */}
        <DailyEntryTable
          dailyEntries={dailyEntries}
          onEdit={handleEditEntry}
          onDelete={handleDeleteEntry}
          onView={handleViewEntry}
          isLoading={isLoading}
        />

        {/* Pagination */}
        {(total || 0) > itemsPerPage && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        {/* Stats */}
        <div className="mt-6 text-sm text-slate-400">
          Showing {dailyEntries.length} of {total || 0} daily entries
          {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
        </div>
      </div>

      {/* Verify Customer Form Modal */}
      {showVerifyForm && (
        <VerifyCustomerForm
          onCancel={handleVerifyCancel}
          onSuccess={handleVerifySuccess}
          onError={handleVerifyError}
          isLoading={isCreatingCustomerEntry}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Daily Entry"
        description="Are you sure you want to delete this daily entry? This action cannot be undone."
        icon={<Trash2 className="h-8 w-8 text-white" />}
        iconBgColor="bg-red-600"
        confirmColor="bg-red-600 hover:bg-red-700"
        confirmText="Delete"
        cancelText="Cancel"
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};
